import React, { useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";

interface EndAssessmentModalProps {
  show: boolean;
  onHide: () => void;
  /** Called with the resolved reason once the user confirms ending the assessment. */
  onConfirm: (reason: string) => void;
}

type EndAction = "discard" | "unable-to-complete";

const EndAssessmentModal: React.FC<EndAssessmentModalProps> = ({
  show,
  onHide,
  onConfirm,
}) => {
  const [action, setAction] = useState<EndAction | "">("");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherText, setOtherText] = useState<string>("");

  const isOtherSelected = selectedReason === ClinicalFormsConstants.otherReasonLabel;

  const resolveReason = (): string => {
    if (action === "discard") {
      return ClinicalFormsConstants.noLongerNeededReason;
    }
    if (action === "unable-to-complete") {
      if (!selectedReason) {
        return "";
      }
      if (isOtherSelected) {
        const text = otherText.trim();
        return text ? `${ClinicalFormsConstants.otherReasonLabel}: ${text}` : "";
      }
      return selectedReason;
    }
    return "";
  };

  const resolvedReason = resolveReason();
  const canConfirm = resolvedReason.length > 0;

  const reset = () => {
    setAction("");
    setSelectedReason("");
    setOtherText("");
  };

  const handleHide = () => {
    reset();
    onHide();
  };

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }
    onConfirm(resolvedReason);
    reset();
  };

  return (
    <Modal show={show} onHide={handleHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>End Assessment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          type="radio"
          id="end-assessment-discard"
          name="end-assessment-action"
          label={
            <>
              <strong>Discard</strong> - {ClinicalFormsConstants.noLongerNeededReason}
            </>
          }
          checked={action === "discard"}
          onChange={() => {
            setAction("discard");
            setSelectedReason("");
            setOtherText("");
          }}
        />
        <Form.Check
          type="radio"
          id="end-assessment-unable"
          name="end-assessment-action"
          label={<strong>Unable To Complete</strong>}
          checked={action === "unable-to-complete"}
          onChange={() => {
            setAction("unable-to-complete");
            setSelectedReason("");
            setOtherText("");
          }}
        />

        {action === "unable-to-complete" && (
          <div className="ms-4 mt-2">
            {ClinicalFormsConstants.unableToCompleteReasons.map((reason) => (
              <Form.Check
                key={reason}
                type="radio"
                id={`utc-reason-${reason}`}
                name="utc-reason"
                label={reason}
                checked={selectedReason === reason}
                onChange={() => setSelectedReason(reason)}
              />
            ))}

            {isOtherSelected && (
              <Form.Control
                as="textarea"
                rows={2}
                className="mt-2"
                placeholder="Please specify a reason"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-end">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary rounded-pill px-6 my-0"
          onClick={handleHide}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-danger rounded-pill px-6 my-0"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          Confirm
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EndAssessmentModal;
